/**
 * Client configuration module for API URLs and environment settings.
 * Reads from Vite environment variables with proper fallbacks.
 *
 * Environment variables (set in .env or .env.local):
 * - VITE_APP_API_URL: Base URL for the S3 Explorer API server
 * - VITE_APP_ENVIRONMENT: Current environment (development/production)
 * - VITE_ONE_DRIVE_REDIRECT_URI: OneDrive OAuth redirect URI
 * - VITE_EMAIL_CRAWLER_API_KEY: `x-api-key` cho các API được gate phía server (crawler +
 *   `/api/bld-reports/config`) — chưa set thì dùng DEFAULT_API_KEY (xem api-key-headers.ts)
 *
 * Production bundle (package nhúng vào frontend host) LUÔN gọi S3 Explorer server trên host
 * đang phục vụ trang (`<origin>/s3-explorer`, vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer`)
 * và bỏ qua `VITE_APP_API_URL`, vì giá trị đó bị bake lúc đóng gói trên máy build (`.env` trỏ
 * `http://localhost:3002`) và sẽ trỏ sai deployment (dev/uat/prod). Dev server (`vite`) không
 * proxy `/s3-explorer` nên vẫn cần `VITE_APP_API_URL`.
 */

const isProduction = import.meta.env.MODE === 'production';

/**
 * Host frontend proxies this S3 Explorer server under `/s3-explorer/`
 * (`location /s3-explorer/` in the host nginx), so the API lives at
 * `<current-host>/s3-explorer/api/...`.
 */
export const DEFAULT_HOST_API_PATH = '/s3-explorer';

/**
 * Resolve the API base URL with this precedence:
 * 1. `VITE_APP_API_URL` — dev server (`vite` không proxy `/s3-explorer` sang server local).
 * 2. `origin` — bundle production: gọi S3 Explorer server ngay trên domain đang phục vụ trang
 *    (`<origin>/s3-explorer/api/...`, vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer/api`)
 *    nên package nhúng vào frontend host không cần cấu hình gì lúc đóng gói.
 * 3. `http://localhost:3000` — no env and no `window` (SSR/tests).
 */
export function resolveApiBaseUrl(envUrl: string | undefined, origin: string | undefined): string {
  if (envUrl) return envUrl;
  if (origin) return `${origin}${DEFAULT_HOST_API_PATH}`;
  return 'http://localhost:3000';
}

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_APP_API_URL;
  const origin = typeof window === 'undefined' ? undefined : window.location.origin;

  // Bundle production (package nhúng vào frontend host / deploy sau nginx): API base LUÔN theo
  // domain đang phục vụ trang — KHÔNG dùng `VITE_APP_API_URL`, vì giá trị này bị bake lúc đóng
  // gói trên máy build (vd `http://localhost:3002` từ `.env`) và trỏ sai deployment (dev/uat/prod).
  // Dev server (MODE=development) không proxy `/s3-explorer` nên vẫn cần env.
  if (isProduction && origin) return `${origin}${DEFAULT_HOST_API_PATH}`;

  const baseUrl = resolveApiBaseUrl(envUrl, origin);

  // Dev server without env: keep the old localhost fallback + warning.
  if (!envUrl && !isProduction) {
    console.warn(
      `[S3Explorer Config] VITE_APP_API_URL not set, using fallback: ${baseUrl}`,
      '\nSet VITE_APP_API_URL in .env or .env.local to configure the API URL.',
    );
  }

  return baseUrl;
}

/**
 * API base URL for the S3 Explorer backend.
 * Production builds call the current host (`<origin>/s3-explorer`) so the prebuilt package
 * embedded into a frontend host works without deployment-specific configuration; dev builds
 * read `VITE_APP_API_URL`.
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Logs current configuration in non-production environments.
 * Call this during app initialization for debugging.
 */
export function logConfig(): void {
  if (!isProduction) {
    console.debug('[S3Explorer Config]', {
      API_BASE_URL,
      VITE_APP_ENVIRONMENT: import.meta.env.VITE_APP_ENVIRONMENT || 'not set',
      MODE: import.meta.env.MODE,
    });
  }
}

export default {
  API_BASE_URL,
  isProduction,
  logConfig,
};
